import React, { useEffect } from 'react';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Box, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import styles from './appearanceSettings.mod.scss'
import { useIntl } from 'react-intl';
import useCreabotsTheme from '@sections/creabots/hooks/useCreabotsTheme';


const AppearanceSettings = () => {

  const intl = useIntl();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { isDarkMode } = useCreabotsTheme()

  // `.theme-labs-dark` (theme-labs.scss) trae el set COMPLETO de variables
  // en modo oscuro, pero el modo claro no tiene una clase propia -- sin
  // `.theme-labs-dark` presente, las variables (`--color-background-three`
  // y el resto) caen al default de `:root` (root.scss), que esta
  // hardcodeado en oscuro. Antes de este fix, "Modo claro" nunca agregaba
  // ninguna clase de tema claro: solo sacaba `.theme-labs-dark`, así que
  // el canvas (via `--color-background-three` en `.blocklyMainBackground`,
  // con `!important`) y el resto del shell quedaban con los valores
  // oscuros de `:root` sin importar la eleccion (RB-128, reportado: "el
  // modo claro no esta funcionando"). `.theme-labs` (sin `-dark`) es el
  // que define el set claro completo -- hay que tenerlo siempre presente,
  // ademas de togglear `.theme-labs-dark` para el modo oscuro.
  //
  // El `setTimeout` no es cosmetico: `App` (containers/app/index.jsx, la
  // raiz de toda la SPA) tiene su propio `useEffect(() => { applyTheme() },
  // [])`, y `cosmos.js#applyTheme` hace `body.removeAttribute('class')`
  // antes de agregar la clase de cosmos -- un borron total, no un
  // classList.remove selectivo. Como los efectos de los componentes HIJOS
  // corren antes que los del padre dentro del mismo commit, si esta clase
  // se agrega de forma sincronica en el mount, `applyTheme` (el padre,
  // corre despues) la borra un instante despues sin que se note en
  // pantalla -- confirmado en vivo: sin el setTimeout, `document.body.
  // className` queda en el theme de cosmos solo (ej. "root"), nunca
  // "theme-labs". Encolar el agregado para el siguiente tick lo corre
  // despues de que TODOS los efectos de montaje (incluido el de App)
  // ya se resolvieron.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      document.body.classList.add('theme-labs');
    }, 0);
    return () => clearTimeout(timeoutId);
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (event: SelectChangeEvent) => {
    const isDarkMode = event.target.value === 'dark';
    const body = document.body;

    body.classList.add('theme-labs');
    (isDarkMode)
      ? body.classList.add('theme-labs-dark')
      : body.classList.remove('theme-labs-dark');
  };

  return (
    <div className={styles.container}>
      <Button
        id="btn-apariencia-modo"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        className={styles.button}
      >
        <PaletteOutlinedIcon className={styles.icon} />
      </Button>

      <Menu
        id="mui-menu-apariencia"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': 'basic-button' }}
        sx={{
          marginTop: '18px',
          marginLeft: '6px',
          padding: 0,
          '& .MuiMenu-list': {
            padding: 0,
            margin: 0,
          },
          '& .MuiMenu-paper': {
            backgroundColor: 'transparent'
          }
        }}
      >
        <Box className={styles.menu} id="menu-apariencia-modo">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
              <PaletteOutlinedIcon />
              <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>
                {intl.formatMessage({ id: 'creabots.appearanceSettings.title' })}
              </Typography>
            </Box>

            <ClearIcon onClick={handleClose} sx={{ cursor: 'pointer' }} className={styles.clearIcon} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
              {intl.formatMessage({ id: 'creabots.appearanceSettings.mode.label' })}
            </Typography>

            <Box sx={{ minWidth: 170 }}>
              <FormControl fullWidth>
                <Select
                  labelId="demo-simple-select-label"
                  id="select-apariencia-modo"
                  onChange={handleChange}
                  size='small'
                  sx={{ borderRadius: '3px', padding: '7px' }}
                  defaultValue={isDarkMode ? 'dark' : 'light'}
                  className={styles.select}
                >
                  <MenuItem id="menuitem-apariencia-light" value={'light'}>{intl.formatMessage({ id: 'creabots.appearanceSettings.mode.light' })}</MenuItem>
                  <MenuItem id="menuitem-apariencia-dark" value={'dark'}>{intl.formatMessage({ id: 'creabots.appearanceSettings.mode.dark' })}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
      </Menu>
    </div>
  )

}

export default AppearanceSettings